import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getManualEvents,
  createManualEvent,
  updateManualEvent,
  deleteManualEvent
} from '../utils/api.js'
import { useFetch } from '../hooks/useFetch.js'
import { hapticImpact, hapticNotification, showConfirm } from '../utils/telegram.js'

const EVENT_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual Event' },
  { value: 'quiz', label: 'Quiz' },
  { value: 'sports', label: 'Sports' },
  { value: 'concert', label: 'Concert' },
  { value: 'workshop', label: 'Workshop' },
  { value: 'meetup', label: 'Meetup' },
  { value: 'tournament', label: 'Tournament' },
  { value: 'other', label: 'Other' }
]

const EMPTY_EVENT = {
  title: '',
  event_type: 'manual',
  description: '',
  source_url: '',
  organizer_name: '',
  organizer_phone: '',
  organizer_language: '',
  organizer_reference: '',
  venue_name: '',
  venue_address: '',
  start_time: '',
  end_time: '',
  price: '',
  is_available: true
}

function FieldGroup({ label, children, required }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, color: 'var(--tg-theme-hint-color)', marginBottom: 4 }}>
        {label}{required && ' *'}
      </label>
      {children}
    </div>
  )
}

function FormInput({ value, onChange, type = 'text', placeholder, required }) {
  return (
    <input
      type={type}
      className="form-input"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid var(--tg-theme-hint-color)',
        background: 'var(--tg-theme-bg-color)',
        color: 'var(--tg-theme-text-color)',
        fontSize: 14,
        boxSizing: 'border-box'
      }}
    />
  )
}

function FormTextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      className="form-textarea"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid var(--tg-theme-hint-color)',
        background: 'var(--tg-theme-bg-color)',
        color: 'var(--tg-theme-text-color)',
        fontSize: 14,
        resize: 'vertical',
        boxSizing: 'border-box'
      }}
    />
  )
}

function FormSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid var(--tg-theme-hint-color)',
        background: 'var(--tg-theme-bg-color)',
        color: 'var(--tg-theme-text-color)',
        fontSize: 14,
        boxSizing: 'border-box'
      }}
    >
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

/**
 * Convert an ISO datetime string to the value format used by datetime-local input.
 * e.g. "2026-08-01T19:00:00.000Z" → "2026-08-01T19:00"
 */
function toDateTimeLocal(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toISOString().slice(0, 16)
  } catch {
    return ''
  }
}

function ManualEventForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_EVENT,
    ...(initial || {}),
    start_time: toDateTimeLocal(initial?.start_time),
    end_time: toDateTimeLocal(initial?.end_time)
  }))
  const [errors, setErrors] = useState({})

  const set = (field) => (value) => setForm(f => ({ ...f, [field]: value }))

  const validate = () => {
    const errs = {}
    if (!form.title.trim()) errs.title = 'Title is required'
    if (form.source_url && !/^https?:\/\/.+/.test(form.source_url)) {
      errs.source_url = 'Must be a valid HTTP or HTTPS URL'
    }
    if (form.organizer_phone && !/^[+\d\s\-()]{7,20}$/.test(form.organizer_phone)) {
      errs.organizer_phone = 'Must be a valid phone number'
    }
    if (form.start_time && form.end_time && new Date(form.end_time) <= new Date(form.start_time)) {
      errs.end_time = 'End time must be after start time'
    }
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return
    const payload = { ...form }
    // Convert datetime-local back to ISO
    if (payload.start_time) payload.start_time = new Date(payload.start_time).toISOString()
    if (payload.end_time) payload.end_time = new Date(payload.end_time).toISOString()
    // Clear empty strings to null
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') payload[k] = null
    })
    onSave(payload)
  }

  const inputStyle = (field) => ({
    ...(errors[field] ? { borderColor: 'var(--ds-status-danger, #e53935)' } : {})
  })

  return (
    <form onSubmit={handleSubmit} aria-label="Manual event form">
      <FieldGroup label="Event Title" required>
        <FormInput
          value={form.title}
          onChange={set('title')}
          placeholder="e.g. Friday Quiz Night"
          required
        />
        {errors.title && <div style={{ color: 'var(--ds-status-danger, #e53935)', fontSize: 12, marginTop: 4 }}>{errors.title}</div>}
      </FieldGroup>

      <FieldGroup label="Event Type">
        <FormSelect value={form.event_type} onChange={set('event_type')} options={EVENT_TYPE_OPTIONS} />
      </FieldGroup>

      <FieldGroup label="Description">
        <FormTextarea value={form.description || ''} onChange={set('description')} placeholder="Describe the event…" />
      </FieldGroup>

      <FieldGroup label="Source / Booking URL">
        <FormInput
          value={form.source_url || ''}
          onChange={set('source_url')}
          placeholder="https://example.com/event"
          type="url"
          style={inputStyle('source_url')}
        />
        {errors.source_url && <div style={{ color: 'var(--ds-status-danger, #e53935)', fontSize: 12, marginTop: 4 }}>{errors.source_url}</div>}
      </FieldGroup>

      <div style={{ borderTop: '1px solid var(--tg-theme-hint-color)', margin: '16px 0', opacity: 0.3 }} />

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--tg-theme-hint-color)' }}>
        Organizer
      </div>

      <FieldGroup label="Organizer Name">
        <FormInput value={form.organizer_name || ''} onChange={set('organizer_name')} placeholder="John Smith" />
      </FieldGroup>

      <FieldGroup label="Organizer Phone">
        <FormInput
          value={form.organizer_phone || ''}
          onChange={set('organizer_phone')}
          placeholder="+49 30 12345678"
          type="tel"
        />
        {errors.organizer_phone && <div style={{ color: 'var(--ds-status-danger, #e53935)', fontSize: 12, marginTop: 4 }}>{errors.organizer_phone}</div>}
      </FieldGroup>

      <FieldGroup label="Organizer Language">
        <FormInput value={form.organizer_language || ''} onChange={set('organizer_language')} placeholder="e.g. en, de, ru" />
      </FieldGroup>

      <FieldGroup label="Reference / Code">
        <FormInput value={form.organizer_reference || ''} onChange={set('organizer_reference')} placeholder="Internal reference or code" />
      </FieldGroup>

      <div style={{ borderTop: '1px solid var(--tg-theme-hint-color)', margin: '16px 0', opacity: 0.3 }} />

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--tg-theme-hint-color)' }}>
        Schedule
      </div>

      <FieldGroup label="Start Date & Time">
        <FormInput
          value={form.start_time || ''}
          onChange={set('start_time')}
          type="datetime-local"
        />
      </FieldGroup>

      <FieldGroup label="End Date & Time">
        <FormInput
          value={form.end_time || ''}
          onChange={set('end_time')}
          type="datetime-local"
        />
        {errors.end_time && <div style={{ color: 'var(--ds-status-danger, #e53935)', fontSize: 12, marginTop: 4 }}>{errors.end_time}</div>}
      </FieldGroup>

      <div style={{ borderTop: '1px solid var(--tg-theme-hint-color)', margin: '16px 0', opacity: 0.3 }} />

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--tg-theme-hint-color)' }}>
        Location
      </div>

      <FieldGroup label="Venue Name">
        <FormInput value={form.venue_name || ''} onChange={set('venue_name')} placeholder="The Pub" />
      </FieldGroup>

      <FieldGroup label="Venue Address">
        <FormInput value={form.venue_address || ''} onChange={set('venue_address')} placeholder="123 Main Street, City" />
      </FieldGroup>

      <div style={{ borderTop: '1px solid var(--tg-theme-hint-color)', margin: '16px 0', opacity: 0.3 }} />

      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--tg-theme-hint-color)' }}>
        Pricing
      </div>

      <FieldGroup label="Price per Person">
        <FormInput value={form.price || ''} onChange={set('price')} placeholder='e.g. "Free" or "10.00" or "€5"' />
      </FieldGroup>

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button
          type="submit"
          className="btn btn-primary"
          style={{ flex: 1 }}
          disabled={saving}
        >
          {saving ? '⏳ Saving…' : (initial ? '💾 Save Changes' : '✅ Create Event')}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ flex: 1 }}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

function EventCard({ event, onEdit, onDelete }) {
  const typeLabel = EVENT_TYPE_OPTIONS.find(o => o.value === event.event_type)?.label || event.event_type
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div className="card-title">{event.title}</div>
          <div className="card-subtitle" style={{ marginTop: 2 }}>
            {typeLabel}
            {event.organizer_name ? ` · ${event.organizer_name}` : ''}
          </div>
          {event.venue_name && (
            <div className="card-subtitle">📍 {event.venue_name}</div>
          )}
          {event.start_time && (
            <div className="card-subtitle">
              🕐 {new Date(event.start_time).toLocaleString('en-GB', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })}
            </div>
          )}
          {event.price && (
            <div className="card-subtitle">💶 {event.price}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 12, flexShrink: 0 }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onEdit(event)}
            aria-label="Edit event"
          >
            ✏️
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onDelete(event)}
            aria-label="Delete event"
            style={{ color: 'var(--ds-status-danger, #e53935)' }}
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  )
}

export default function ManualEventsPage() {
  const navigate = useNavigate()
  const [editingEvent, setEditingEvent] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [alert, setAlert] = useState(null)
  const [saving, setSaving] = useState(false)

  const { data, loading, error, refetch } = useFetch(() => getManualEvents())
  const events = data?.data || []

  const handleSave = async (form) => {
    setSaving(true)
    try {
      if (editingEvent) {
        await updateManualEvent(editingEvent.id, form)
        setAlert({ type: 'success', text: 'Event updated!' })
      } else {
        await createManualEvent(form)
        setAlert({ type: 'success', text: 'Event created!' })
      }
      hapticNotification('success')
      setShowForm(false)
      setEditingEvent(null)
      refetch()
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Save failed' })
      hapticNotification('error')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (event) => {
    setEditingEvent(event)
    setShowForm(true)
    hapticImpact('light')
  }

  const handleDelete = async (event) => {
    const confirmed = await showConfirm(`Delete "${event.title}"?`)
    if (!confirmed) return
    hapticImpact('medium')
    try {
      await deleteManualEvent(event.id)
      setAlert({ type: 'success', text: 'Event deleted' })
      refetch()
    } catch (err) {
      setAlert({ type: 'error', text: err?.response?.data?.error || 'Delete failed' })
    }
  }

  return (
    <div>
      <button
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 16, marginBottom: 8, width: 'auto' }}
        onClick={() => navigate('/settings')}
      >
        ← Back
      </button>

      <h2 className="section-title">Manual Events</h2>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          {alert.text}
          <button
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setAlert(null)}
          >
            ✕
          </button>
        </div>
      )}

      {!showForm && (
        <button
          className="btn btn-primary"
          style={{ marginBottom: 16 }}
          onClick={() => { setEditingEvent(null); setShowForm(true) }}
        >
          + Create Event
        </button>
      )}

      {showForm && (
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>
            {editingEvent ? '✏️ Edit Event' : '➕ New Manual Event'}
          </h3>
          <ManualEventForm
            initial={editingEvent}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingEvent(null) }}
            saving={saving}
          />
        </div>
      )}

      {loading && <div className="status-message"><div className="loading-spinner" /></div>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && events.length === 0 && !showForm && (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-title">No manual events yet</div>
          <div className="empty-state-text">
            Create an event to make it appear in the app alongside parser-generated events.
          </div>
        </div>
      )}

      {events.map(event => (
        <EventCard
          key={event.id}
          event={event}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ))}

      {events.length > 0 && (
        <div className="card" style={{ marginTop: 8, fontSize: 13, color: 'var(--tg-theme-hint-color)' }}>
          <p>📌 Manual events appear in the Events tab alongside regular events.</p>
          <p style={{ marginTop: 8 }}>🔔 Users can subscribe to receive notifications for these events.</p>
        </div>
      )}
    </div>
  )
}
